/**
 * The "run on open" preference for the ask panel (TRA-1081).
 *
 * On, the panel asks the run-on-open question the moment a queue item
 * loads, so the first answer is there before the reviewer reads the trace.
 * Off, the question only sits pre-filled in the composer. Each open spends
 * one model call, so a reviewer skimming rows flips this off.
 *
 * Stored per browser in localStorage, not per team: it is a reviewer's
 * pace, not customer content, and one teammate skimming must not silence
 * the answer for another who reads every row.
 *
 * Three guards keep the switch from burning the team's hourly turn cap
 * (`TURNS_PER_HOUR` server-side) on rows nobody reads:
 * - a row with a saved thread was already asked; it is not asked again;
 * - a row that already has a verdict (reviewed or dismissed) is not asked;
 * - the question waits `RUN_ON_OPEN_DELAY_MS` after mount, so j/k skimming
 *   past a row fires nothing. The server counts a turn when the request
 *   arrives, so aborting an in-flight one would still spend it.
 */

import type { ReviewQueueItem } from '@/api/generated/model'
import { readStoredFlag, writeStoredFlag } from '@/lib/storedFlag'

export const RUN_ON_OPEN_STORAGE_KEY = 'review-chat:run-on-open'

/**
 * How long a row must stay on screen before the question is sent. Long
 * enough that j/k past a row costs nothing, short enough that a reader is
 * still on the transcript's first lines when the answer starts.
 */
export const RUN_ON_OPEN_DELAY_MS = 1_500

/** Anything but an explicit "off" is on: the ticket's default. */
export function loadRunOnOpen(): boolean {
  return readStoredFlag(RUN_ON_OPEN_STORAGE_KEY, true)
}

export function saveRunOnOpen(enabled: boolean): void {
  writeStoredFlag(RUN_ON_OPEN_STORAGE_KEY, enabled)
}

/**
 * Rows that still need a human verdict. A held row (judge budget hold) is
 * still waiting for one, so it counts; reviewed and dismissed rows already
 * have theirs and are opened only to re-read.
 */
export function awaitsVerdict(reviewStatus: ReviewQueueItem['review_status']): boolean {
  return reviewStatus === 'unreviewed' || reviewStatus === 'held'
}

export interface RunOnOpenInput {
  enabled: boolean
  /** The thread restored for this item; a non-empty one was already asked. */
  messageCount: number
  /** Impersonating staff and restricted demo teams are refused the POST. */
  readOnly: boolean
  /** Rows with a verdict already are pre-filled, never auto-asked. */
  reviewStatus: ReviewQueueItem['review_status']
}

/** Whether mounting the panel should ask the run-on-open question. */
export function shouldRunOnOpen({
  enabled,
  messageCount,
  readOnly,
  reviewStatus,
}: RunOnOpenInput): boolean {
  return enabled && messageCount === 0 && !readOnly && awaitsVerdict(reviewStatus)
}
